import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useToast } from '@/contexts/ToastContext';
import { getRecallSimulation, getBatchSuggestions } from '@/services/traceabilityService';

export default function RecallSimulationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const prefill = (location.state as { batchNumber?: string; batchType?: string }) || null;

  const [searchTerm, setSearchTerm] = useState(prefill?.batchNumber || '');
  const [batchType, setBatchType] = useState<'raw' | 'finished'>(prefill?.batchType === 'finished' ? 'finished' : 'raw');
  const [selectedBatch, setSelectedBatch] = useState<string | null>(prefill?.batchNumber || null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const [impactResults, setImpactResults] = useState<{
    inputItem: string;
    totalUsed: number;
    totalProduced: number;
    impactLevel: 'HIGH' | 'MEDIUM' | 'LOW';
    impactColor: string;
    affectedPOs: Array<{
      poNumber: string;
      product: string;
      outputQty: number;
      outputBatch: string;
      unit: string;
      date: string;
    }>;
    mockAffectedCustomers: Array<{
      invoiceNo: string;
      customer: string;
      phone?: string;
      qtySold: number;
      date: string;
    }>;
  } | null>(null);

  useEffect(() => {
    async function loadSuggestions() {
      try {
        const res = await getBatchSuggestions(batchType === 'raw' ? 'forward' : 'backward', searchTerm);
        if (res && res.success) {
          setSuggestions(res.data || []);
        }
      } catch (err) {
        console.error('Failed to load batch suggestions:', err);
      }
    }
    loadSuggestions();
  }, [searchTerm, batchType]);

  const handleSearch = async (batch?: string) => {
    const target = batch || searchTerm.trim();
    if (!target) return;

    setSelectedBatch(target);
    setShowSuggestions(false);
    setLoading(true);

    try {
      const res = await getRecallSimulation(target, batchType);
      if (res && res.success && res.data) {
        setImpactResults({
          inputItem: res.data.inputItem,
          totalUsed: res.data.totalUsed,
          totalProduced: res.data.totalProduced,
          impactLevel: res.data.impactLevel,
          impactColor: res.data.impactColor,
          affectedPOs: res.data.affectedPOs,
          mockAffectedCustomers: res.data.affectedCustomers || [],
        });
      } else {
        setImpactResults(null);
      }
    } catch (err) {
      console.error('Failed to run recall simulation:', err);
      setImpactResults(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCsvDownload = () => {
    if (!impactResults) return;
    const rows = [['Batch Number', 'Product', 'Customer', 'Invoice', 'Date', 'Action Required']];
    impactResults.mockAffectedCustomers.forEach((c, i) => {
      rows.push([selectedBatch!, impactResults.affectedPOs[i]?.product || 'Unknown', c.customer, c.invoiceNo, c.date, 'Contact customer immediately']);
    });
    const csv = rows.map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `RecallSimulation_${selectedBatch}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Recall report CSV downloaded');
  };

  return (
    <AppLayout>
      <div className="min-h-screen bg-[#f8fafc] py-6 px-4 md:px-6 w-full">
        <div className="w-full">
          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={() => navigate('/manufacturing/traceability')}
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-700 cursor-pointer shadow-sm transition-colors"
              title="Back to Batch Traceability"
            >
              <i className="ri-arrow-left-line text-lg" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-[#1e293b]">Recall Simulation</h1>
              <p className="text-sm text-[#64748b] mt-0.5">Identify all affected products if a batch is defective</p>
            </div>
          </div>

          {/* Warning Banner */}
          <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 mb-6 flex items-center gap-3 shadow-sm">
            <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-rose-100 shrink-0">
              <i className="ri-error-warning-fill text-xl text-rose-600" />
            </div>
            <div>
              <div className="text-sm font-bold text-rose-800">THIS IS A SIMULATION</div>
              <div className="text-xs text-rose-600 font-medium">No actual recall has been initiated. This is for analysis purposes only.</div>
            </div>
          </div>

          {/* Step 1 — Select Batch */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-6 shadow-sm">
            <h3 className="text-sm font-bold text-[#1e293b] mb-4">Step 1 — Select Suspect Batch</h3>

            {/* Type Toggle */}
            <div className="flex items-center gap-1.5 bg-slate-100 border border-slate-200 rounded-xl p-1 mb-4 w-fit shadow-xs">
              <button
                onClick={() => { setBatchType('raw'); setSelectedBatch(null); setSearchTerm(''); }}
                className={`px-4 py-2 rounded-lg text-xs transition-all cursor-pointer whitespace-nowrap ${
                  batchType === 'raw'
                    ? 'bg-[#2563eb] text-white font-semibold shadow-sm'
                    : 'bg-slate-50 hover:bg-slate-100 text-slate-600 font-medium border border-slate-200'
                }`}
              >
                Raw Material Batch
              </button>
              <button
                onClick={() => { setBatchType('finished'); setSelectedBatch(null); setSearchTerm(''); }}
                className={`px-4 py-2 rounded-lg text-xs transition-all cursor-pointer whitespace-nowrap ${
                  batchType === 'finished'
                    ? 'bg-[#2563eb] text-white font-semibold shadow-sm'
                    : 'bg-slate-50 hover:bg-slate-100 text-slate-600 font-medium border border-slate-200'
                }`}
              >
                Finished Goods Batch
              </button>
            </div>

            <div className="flex items-stretch gap-3">
              <div className="flex-1 relative">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400">
                  <i className="ri-barcode-line text-base" />
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setShowSuggestions(true); }}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                  placeholder={`Enter ${batchType === 'raw' ? 'raw material' : 'finished goods'} batch number`}
                  className="w-full pl-10 pr-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-100 focus:border-rose-300 font-medium"
                />
                {showSuggestions && searchTerm && suggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-20 max-h-48 overflow-y-auto">
                    {suggestions.map((b) => (
                      <div
                        key={b}
                        onMouseDown={() => { setSearchTerm(b); handleSearch(b); }}
                        className="px-4 py-2.5 text-sm text-[#1e293b] hover:bg-rose-50 cursor-pointer flex items-center gap-2 font-medium"
                      >
                        <i className="ri-barcode-box-line text-rose-500" />
                        {b}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={() => handleSearch()}
                className="px-6 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap shadow-sm inline-flex items-center gap-2"
              >
                <i className="ri-search-line" /> Analyze Impact
              </button>
            </div>
          </div>

          {/* Step 2 — Impact Analysis */}
          {selectedBatch && !impactResults && (
            <div className="bg-white border border-amber-200 rounded-2xl p-8 text-center shadow-sm">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-amber-50 border border-amber-200 mb-4">
                <i className="ri-error-warning-line text-2xl text-amber-500" />
              </div>
              <h3 className="text-lg font-bold text-[#1e293b] mb-1">No Records Found</h3>
              <p className="text-sm text-[#64748b]">Batch &quot;{selectedBatch}&quot; has no traceability data</p>
            </div>
          )}

          {impactResults && (
            <div className="space-y-5">
              {/* RED ALERT CARD */}
              <div className="bg-rose-50 border-2 border-rose-300 rounded-2xl p-5 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-rose-200 shrink-0">
                    <i className="ri-alert-fill text-3xl text-rose-700" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-lg font-bold text-rose-800 mb-1">RECALL SIMULATION RESULTS</h2>
                    <div className="flex flex-wrap gap-3 text-sm">
                      <span className="bg-rose-100 px-2.5 py-1 rounded-md text-rose-800 font-bold border border-rose-200">Suspect: {selectedBatch}</span>
                      <span className="bg-rose-100 px-2.5 py-1 rounded-md text-rose-800 font-semibold border border-rose-200">{impactResults.inputItem}</span>
                      <span className={`px-2.5 py-1 rounded-md font-bold border ${impactResults.impactColor === 'HIGH' ? 'bg-rose-200 text-rose-900 border-rose-300' : 'bg-amber-200 text-amber-900 border-amber-300'}`}>
                        Impact Level: {impactResults.impactLevel}
                      </span>
                    </div>
                    {impactResults.impactLevel === 'HIGH' && (
                      <p className="text-xs text-rose-700 mt-2 font-medium">This batch was used in multiple products — all downstream products may be affected.</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Affected Production Orders */}
              <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                <div className="px-5 py-4 border-b border-slate-200 bg-rose-50/50">
                  <h3 className="text-sm font-bold text-[#1e293b]">
                    <i className="ri-stack-line mr-2 text-rose-600" /> Affected Production Orders
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50/70 border-b border-slate-200">
                        <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider">PO Number</th>
                        <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider">Product</th>
                        <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider">Qty</th>
                        <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider">Date</th>
                        <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider">Output Batch</th>
                      </tr>
                    </thead>
                    <tbody>
                      {impactResults.affectedPOs.map((po, i) => (
                        <tr key={i} className="border-b border-slate-100 hover:bg-slate-50/60 transition-colors">
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-[#2563eb]">{po.poNumber}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-[#1e293b]">{po.product}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-[#1e293b]">{po.outputQty} {po.unit}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-xs text-[#64748b]">{po.date}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-xs text-emerald-700 font-bold">{po.outputBatch}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Affected Customers */}
              <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                <div className="px-5 py-4 border-b border-slate-200 bg-slate-50">
                  <h3 className="text-sm font-bold text-[#1e293b]">
                    <i className="ri-group-line mr-2 text-slate-500" /> Potentially Affected Customers
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50/70 border-b border-slate-200">
                        <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider">Invoice No</th>
                        <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider">Customer</th>
                        <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider">Qty Sold</th>
                        <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {impactResults.mockAffectedCustomers.map((c, i) => (
                        <tr key={i} className="border-b border-slate-100 hover:bg-slate-50/60 transition-colors">
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-[#2563eb]">{c.invoiceNo}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-[#1e293b]">{c.customer}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-[#1e293b]">{c.qtySold}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-xs text-[#64748b]">{c.date}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="px-5 py-3 bg-amber-50 border-t border-amber-200 text-xs font-medium text-amber-800 flex items-center gap-2">
                  <i className="ri-information-line text-amber-600 text-sm" />
                  Sales invoice links are estimated. Verify against actual sales records for complete accuracy.
                </div>
              </div>

              {/* Recall Actions Panel */}
              <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                <h3 className="text-sm font-bold text-[#1e293b] mb-4">
                  <i className="ri-flashlight-line mr-2 text-rose-600" /> Recall Actions
                </h3>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => toast.success('Notification sent to affected customers (simulated)')}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap shadow-sm"
                  >
                    <i className="ri-mail-send-line" />
                    Notify Affected Customers
                  </button>
                  <button
                    onClick={handleCsvDownload}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap shadow-sm"
                  >
                    <i className="ri-download-2-line" />
                    Generate Recall Report
                  </button>
                  <button
                    onClick={() => toast.success(`Batch ${selectedBatch} has been flagged (simulated)`)}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap shadow-sm"
                  >
                    <i className="ri-forbid-line" />
                    Block Suspect Batch
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}